<script setup lang="ts">
import DoNotContactChip from '@/components/Common/DoNotContactChip.vue';
import DoNotContactPopover from '@/components/Conversations/DoNotContact/DoNotContactPopover.vue';
import UnlockedLockSvg from '@/assets/svg/unlocked-lock-20.svg?component';
import SparkleIcon from '@/assets/svg/sparkle.svg?component';
import ProPreviewStats from '@/components/Search/ProPreviewCard/ProPreviewStats.vue';
import ProApplicantList from '@/components/Search/ProPreviewCard/ProApplicantList.vue';
import WorkExperienceList from '@/components/Search/ProPreviewCard/WorkExperienceList.vue';
import EducationList from '@/components/Search/ProPreviewCard/ProEducationList.vue';

import Checkbox from '@/components/Shared/Input/Checkbox/Checkbox.vue';
import SubscriptionService from '@/core/shared/subscription/subscription.service';
import type { SearchGatedUserProfile } from '@/core/sourcing/search/types/search-gated-user-profile.type';
import { computed, ref } from 'vue';
import { MessagingService } from '@/core/sourcing/messaging/messaging.service';
import { MessagingSource } from '@/core/sourcing/messaging/types/messaging-source.type';
import { ProUnlockSource } from '@/core/sourcing/pro-unlock/types/pro-unlock-source.type';
import { ViewedCandidateService } from '@/core/sourcing/viewed-candidates/viewed-candidate.service';
import { getProActivityRelativeDateTime, isProRecentlyActive } from '@/core/sourcing/utils/dates';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { getHighlightedName } from '@/core/sourcing/search/utils/get-highlighted-name.util';
import { getNameForCandidate } from '@/core/sourcing/search/utils/get-name-for-candidate.util';
import JobApplicantService from '@/core/shared/job-applicant/job-applicant.service';
import { UNLOCK_LIMIT_REACHED_TOAST } from '@/core/sourcing/pro-unlock/consts/unlock-limit-reached-toast.const';
import { isUnlockLimitError } from '@/core/sourcing/pro-unlock/utils/is-unlock-limit-error.util';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import ProUnlockService from '@/core/sourcing/pro-unlock/pro-unlock.service';
import { useProUnlockStore } from '@/core/sourcing/pro-unlock/pro-unlock.store';
import EnrichmentFieldValue from '@/components/Common/EnrichmentFieldValue.vue';
import EnrichmentProgress from '@/components/Common/EnrichmentProgress.vue';
import EnrichmentUnlockButton from '@/components/Common/EnrichmentUnlockButton.vue';
import CandidateInsightsChips from '@/components/Search/ProPreviewCard/CandidateInsightsChips.vue';
import { filteredInsightFields } from '@/components/Search/ProPreviewCard/candidate-insights-chips';
import { SearchService as SharedSearchService } from '@/core/shared/search/search.service';
import { toSafePlainHtml } from '@/utils/strings/safe-highlight-html.util';
import { isTextSelected } from '@/utils/dom/text-selection.utils';
import { SearchService } from '@/core/sourcing/search/search.service';
import { SearchTab } from '@/core/sourcing/search/types/search-tab.enum';
import SkipForwardIcon from '@/assets/svg/sourcing/skip-forward.svg?component';
import ReverseLeftIcon from '@/assets/svg/sourcing/reverse-left.svg?component';
import { SkippedProsService } from '@/core/sourcing/skipped-pros/skipped-pros.service';
import { useMeStore } from '@/core/shared/me/me.store';
import LockUnlockedSvg from '@/assets/svg/lock-unlocked-20.svg?component';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { UpgradeToGoldVariant } from '@/core/shared/dialog/dialog.types';
import { EmployerFeatureGatingService } from '@/core/shared/employer-feature-gating/employer-feature-gating.service';
import { useEnrichmentAnimation } from '@/composables/useEnrichmentAnimation';
import { type EnrichmentRunSummary, FeatureName } from '@factoryfixinc/ats-interfaces';

const props = withDefaults(
  defineProps<{
    pro: SearchGatedUserProfile;
    selectedProId?: number;
    // Smart searches — natural-language and job — rank by document/semantic fit rather
    // than the recruiter's literal keywords. Drives two things: the rank-1 "Top match"
    // pill, and highlight suppression (see `suppressHighlights` for the why). Keyword/
    // Boolean — the only non-smart mode — keeps highlights.
    isSmart?: boolean;
    // 1-based position of this card in the result set; the parent derives it from
    // pagination. Only `rank === 1` earns the "Top match" pill.
    rank?: number;
  }>(),
  {
    isSmart: false,
    rank: undefined,
  },
);

const emit = defineEmits<{
  (e: 'click'): void;
  (e: 'open:messaging', value: boolean): void;
  (e: 'open:resume'): void;
  (e: 'click:enrichment', result: EnrichmentRunSummary): void;
}>();

const dialogService = new DialogService();
const gatingService = new EmployerFeatureGatingService();
const subscriptionService = new SubscriptionService();
const messagingService = new MessagingService();
const viewedCandidateService = new ViewedCandidateService();
const jobApplicantService = new JobApplicantService();
const proUnlockService = new ProUnlockService();
const searchService = new SearchService();
const sharedSearchService = new SharedSearchService();
const skippedProsService = new SkippedProsService();
const meStore = useMeStore();
const proUnlockStore = useProUnlockStore();

const profileIdAsNumber = computed(() => Number(props.pro.profileId));
const enrichmentOutcome = computed(() => proUnlockStore.outcomeFor(profileIdAsNumber.value));
const enrichmentFieldStates = computed(() => proUnlockStore.fieldStates[profileIdAsNumber.value]);
const enrichmentResult = computed(() => proUnlockStore.results[profileIdAsNumber.value] ?? null);
const currentPhone = computed(() => props.pro.phone ?? '');
const currentEmail = computed(() => props.pro.email ?? '');
const {
  displayedEmail,
  displayedPhone,
  handleResolved: handleEnrichmentResolved,
  isActive: isEnrichmentAnimationActive,
  isButtonPending: isEnrichmentButtonPending,
  isEmailConfirmed,
  isPhoneConfirmed,
} = useEnrichmentAnimation({
  outcome: enrichmentOutcome,
  fieldStates: enrichmentFieldStates,
  phone: currentPhone,
  email: currentEmail,
});
const displayedAddress = computed(() => formattedAddress.value);
const hasDisplayedContact = computed(
  () => !!(displayedPhone.value || displayedEmail.value || displayedAddress.value),
);

const isUnlocked = computed(() => props.pro.unlocked);

const isUnlockingCandidate = ref(false);
const hasTalentPoolAccess = computed(() =>
  gatingService.hasAccess(FeatureName.TALENT_POOL_UNLOCKS),
);

const unlockButtonText = computed(() =>
  hasTalentPoolAccess.value ? 'Unlock' : 'Unlock with Gold',
);

// TODO: Remove isGatedSubscription check in next subscription service refactor
// This check is a legacy from v1/v2 subscription model separation
const isUnlockedIconVisible = computed(
  () => isUnlocked.value && subscriptionService.isGatedSubscription,
);
const showUnlockedActions = computed(
  () => isUnlockedIconVisible.value && !isEnrichmentButtonPending.value,
);
const isUnlockInteractionPending = computed(
  () => isUnlockingCandidate.value || isEnrichmentButtonPending.value,
);

const viewedOn = computed(
  () => viewedCandidateService.viewedCandidatesRecord?.[Number(props.pro.profileId)],
);

const jobApplicants = computed(() => {
  return jobApplicantService.getJobApplicantsByUserProfile(Number(props.pro.profileId));
});

const isWarm = computed(() => isProRecentlyActive(props.pro.lastActiveTs));

/**
 * Highlight computeds below read from _esHighlight (Elastic). Fragments
 * contain <b> tags around matched terms (e.g. "<b>CNC</b> Machinist").
 * These strings are rendered via v-html and styled by ppc_highlighted_text
 * CSS. When matching fragments back to source data, tags are stripped for
 * comparison but preserved in the output for display.
 */
// Both smart modes (natural-language and job) suppress keyword-style highlight markup:
// their effective query — the AI-cleaned text or the job document — diverges from the
// recruiter's literal terms, so highlighting reads as a mismatch. Keyword/Boolean, where
// the matches ARE the typed terms, keeps highlights. (NL: ONE-6358; job: ONE-6457.)
const suppressHighlights = computed(() => props.isSmart);

const highlightedName = computed(() =>
  suppressHighlights.value
    ? toSafePlainHtml(getNameForCandidate(props.pro))
    : getHighlightedName(props.pro),
);

// The single best fit result earns the "Top match" pill in smart searches.
const showTopMatch = computed(() => props.isSmart && props.rank === 1);

const isSelectedForBulkMessaging = computed(() =>
  messagingService.selectedPPCUserProfiles.some(
    (userProfile) => userProfile.id === Number(props.pro.profileId),
  ),
);

const formattedAddress = computed(() => {
  const { city, state, postalCode } = props.pro.address || {};
  return [city, state, postalCode].filter(Boolean).join(', ');
});

const candidateSubtitle = computed(() => {
  const subtitle = [props.pro.historyJobTitle, formattedAddress.value].filter(Boolean).join(' • ');
  return subtitle || undefined;
});

const handleBulkMessagingSelection = async () => {
  try {
    if (isSelectedForBulkMessaging.value) {
      messagingService.removeProFromSelectedUserProfiles(Number(props.pro.profileId));
      return;
    }

    await messagingService.addProUserProfileToSelectedList({
      profileId: props.pro.profileId,
      nameFirst: props.pro.nameFirst,
      nameLast: props.pro.nameLast,
      unlocked: props.pro.unlocked,
      doNotContact: props.pro.doNotContact,
      lastActiveTs: props.pro.lastActiveTs,
      source: MessagingSource.PPC,
      subtitle: candidateSubtitle.value,
    });
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Failed to add candidate to bulk messaging');
  }
};

const isExpanded = ref(false);

/**
 * Handles the click event on the ProPreviewCard.
 * Only toggles the expanded state if the user is not selecting text,
 * allowing users to copy content without triggering the expand/collapse action.
 */
const handlePPCardClick = () => {
  if (!isTextSelected()) {
    isExpanded.value = !isExpanded.value;
  }
};

const handleUnlockProClick = () => {
  if (hasTalentPoolAccess.value) {
    unlockProUserProfile();
  } else {
    dialogService.openUpgradeToGoldModal(UpgradeToGoldVariant.TalentSearch);
  }
};

const unlockProUserProfile = async () => {
  if (!meStore.employer?.id) return;
  isUnlockingCandidate.value = true;
  try {
    const userProfileIdToUnlock = Number(props.pro.profileId);
    if (!userProfileIdToUnlock) {
      return;
    }

    const searchSessionId = searchService.searchSessionId;

    // saves unlock in firestore
    await proUnlockService.unlockProUserProfiles(
      [userProfileIdToUnlock],
      ProUnlockSource.PPP,
      searchSessionId,
    );

    // After unlock, refresh the search results
    const result = await searchService.searchCandidates();
    await jobApplicantService.getJobApplicantsByUserProfileIds(
      meStore.employer.id,
      result.hits.map((candidate) => Number(candidate.profileId)),
    );
  } catch (error) {
    ErrorService.captureException(error);
    if (isUnlockLimitError(error)) {
      SnackbarService.criticalAlt(UNLOCK_LIMIT_REACHED_TOAST);
    } else {
      SnackbarService.critical('Could not unlock user profile, please try again later.');
    }
  } finally {
    isUnlockingCandidate.value = false;
    trackUnlock();
  }
};

function trackUnlock() {
  TrackingService.instance.trackActionEvent(TrackingActionName.UNLOCK, {
    recently_active: isProRecentlyActive(props.pro.lastActiveTs),
  });
}

const openMessaging = async () => {
  try {
    const userProfileIdToUnlock = props.pro.profileId;
    if (!userProfileIdToUnlock || props.pro.doNotContact === true) return;

    messagingService.selectedProUserProfiles = [];
    await messagingService.addProUserProfileToSelectedList({
      profileId: userProfileIdToUnlock.toString(),
      nameFirst: props.pro.nameFirst ?? '',
      nameLast: props.pro.nameLast ?? '',
      unlocked: props.pro.unlocked,
      doNotContact: props.pro.doNotContact,
      lastActiveTs: props.pro.lastActiveTs ?? '',
      source: MessagingSource.PPC,
      subtitle: candidateSubtitle.value,
    });
    emit('open:messaging', true);
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not unlock user profile');
  }
};

const isViewResumeVisible = computed(() => props.pro.unlocked && !!props.pro.resumeUrl);

const isInSkippedTab = computed(() => searchService.activeTab === SearchTab.SKIPPED);

// Filtered fields' chips go first so the chip cap can't hide what the search is filtering on.
const prioritizedInsightFields = computed(() =>
  filteredInsightFields((type) => sharedSearchService.isDossierFilterActive(type)),
);

const handleSkipPro = async () => skippedProsService.skipPro(Number(props.pro.profileId));

const handleRestorePro = async () => skippedProsService.restorePro(Number(props.pro.profileId));

const toggleSkipStatus = () => (isInSkippedTab.value ? handleRestorePro() : handleSkipPro());
</script>

<template>
  <div
    :id="`pro-preview-card-${pro.profileId}`"
    :class="{
      '!border-tint-200': isExpanded,
    }"
    @click="handlePPCardClick"
  >
    <!-- Header stacks vertically on mobile, reverts to a justified single row from sm up -->
    <div class="flex flex-col gap-2 sm:flex-row sm:justify-between sm:gap-0">
      <div class="flex min-w-0 flex-wrap items-center gap-y-1">
        <!-- Multiselect Checkbox -->
        <Checkbox
          class="bulk-selection-checkbox mr-0.5"
          :class="{ '!ml-0 !opacity-100': isSelectedForBulkMessaging }"
          :model-value="isSelectedForBulkMessaging"
          @click.stop="handleBulkMessagingSelection"
        />
        <!-- Unlocked icon — hidden on mobile, where the always-visible bulk
             checkbox already occupies the leading slot (avoids a checkbox + lock
             glyph cluster); shown from sm up exactly as before. -->
        <LockUnlockedSvg
          v-if="isUnlockedIconVisible"
          class="ml-1.5 mr-1 hidden text-tint-400 sm:block"
        />

        <!-- Candidate Name -->
        <span
          data-test-id="pro-name"
          class="sensitive ppc_highlighted_text text-[16px] font-bold leading-5"
          :class="isUnlockedIconVisible ? 'ml-2 sm:ml-0' : 'ml-2'"
          v-html="highlightedName"
        />

        <DoNotContactPopover
          v-if="pro.doNotContact === true && meStore.employer?.id"
          :employer-id="meStore.employer.id"
          :user-profile-id="profileIdAsNumber"
          :candidate-name="getNameForCandidate(pro)"
          source="Talent Search"
          class="ml-2"
          @removed="
            (employerId, userProfileId) =>
              searchService.patchDoNotContact(employerId, userProfileId, false)
          "
        />
        <DoNotContactChip v-else-if="pro.doNotContact === true" class="ml-2" />

        <!-- Last Active Indicator -->
        <div
          v-if="isWarm"
          data-test-id="pro-is-warm"
          class="ml-2 flex h-6 items-center justify-center rounded-[10px] bg-primary-0 px-2 py-0.5"
        >
          <span class="text-xs font-bold leading-[18px] text-primary-600">
            {{ getProActivityRelativeDateTime(pro.lastActiveTs) }}
          </span>
        </div>

        <!-- Top match pill: only the rank-1 result of a smart (NL/job) search -->
        <div
          v-if="showTopMatch"
          data-test-id="pro-top-match"
          class="ml-2 flex h-6 shrink-0 items-center gap-1 rounded-full border border-highlight-100 bg-highlight-50 px-2 py-0.5"
        >
          <SparkleIcon class="h-3 w-3 text-highlight-600" viewBox="0 0 16 20" />
          <span class="text-xs font-bold leading-[18px] text-highlight-600">Top match</span>
        </div>
      </div>

      <!-- flex-wrap lets the actions wrap instead of overflowing once stacked on mobile -->
      <div class="relative flex min-h-9 flex-wrap items-center gap-2">
        <!-- Skip/Restore button -->
        <v-btn
          class="skip-restore-button modal-button-secondary mr-2"
          @click.prevent.stop="toggleSkipStatus"
        >
          <div class="btn-content">
            <SkipForwardIcon v-if="!isInSkippedTab" />
            <ReverseLeftIcon v-else />
            <span>{{ isInSkippedTab ? 'Restore' : 'Skip' }}</span>
          </div>
        </v-btn>

        <!-- Candidate Unlocked buttons -->
        <Transition
          enter-active-class="transition-opacity duration-500 ease-out motion-reduce:transition-none"
          leave-active-class="absolute right-0 top-0 transition-opacity duration-500 ease-out motion-reduce:transition-none"
          enter-from-class="opacity-0"
          leave-to-class="opacity-0"
        >
          <div v-if="showUnlockedActions" key="unlocked" data-test-id="pro-unlocked-icon">
            <v-btn
              v-if="isViewResumeVisible"
              data-test-id="view-resume"
              :ripple="false"
              class="modal-button-secondary mr-3 sm:mr-2"
              variant="flat"
              density="compact"
              @click.stop.prevent="emit('open:resume')"
              >View resume</v-btn
            >
            <v-btn
              v-else
              :ripple="false"
              class="modal-button-secondary mr-3 disabled:!border-tint-60 disabled:!bg-white disabled:!text-tint-400 sm:mr-2"
              variant="flat"
              density="compact"
              disabled
              >No resume</v-btn
            >
            <v-btn
              :ripple="false"
              class="modal-button-secondary disabled:!border-tint-60 disabled:!bg-white disabled:!text-tint-400"
              variant="flat"
              density="compact"
              :disabled="pro.doNotContact === true"
              data-test="add-to-job"
              @click.stop.prevent="openMessaging"
              >Add to job</v-btn
            >
          </div>
          <!-- Candidate locked button -->
          <div v-else key="locked" data-test-id="pro-locked-button">
            <EnrichmentUnlockButton
              v-if="hasTalentPoolAccess"
              :loading="isUnlockInteractionPending"
              @click="handleUnlockProClick"
            >
              <template #icon>
                <UnlockedLockSvg class="h-5 w-5" />
              </template>
            </EnrichmentUnlockButton>
            <v-btn
              v-else
              :ripple="false"
              class="modal-button-primary unlock-gold"
              variant="flat"
              :prepend-icon="UnlockedLockSvg"
              density="compact"
              @click.stop.prevent="handleUnlockProClick"
            >
              {{ unlockButtonText }}
            </v-btn>
          </div>
        </Transition>
      </div>
    </div>
    <!-- Candidate Contact Info -->
    <div
      class="sensitive mt-0 flex flex-wrap items-center gap-2 text-xs font-normal leading-4.5 text-shade-800"
    >
      <EnrichmentFieldValue
        v-if="displayedPhone"
        :value="displayedPhone"
        :checking="isEnrichmentAnimationActive"
        :confirmed="isPhoneConfirmed"
      />
      <span v-if="displayedPhone && (displayedEmail || displayedAddress)" aria-hidden="true"
        >•</span
      >
      <EnrichmentFieldValue
        v-if="displayedEmail"
        :value="displayedEmail"
        :checking="isEnrichmentAnimationActive"
        :confirmed="isEmailConfirmed"
      />
      <span v-if="displayedEmail && displayedAddress" aria-hidden="true">•</span>
      <span v-if="displayedAddress" class="min-w-0 break-words">{{ displayedAddress }}</span>
      <template v-if="enrichmentOutcome !== null">
        <span v-if="hasDisplayedContact" aria-hidden="true">•</span>
        <EnrichmentProgress
          :outcome="enrichmentOutcome"
          :field-states="enrichmentFieldStates"
          :result="enrichmentResult"
          @resolved="handleEnrichmentResolved"
          @click="(result) => emit('click:enrichment', result)"
        />
      </template>
    </div>
    <!-- Candidate memory chips: shown only when the hit actually carries insights. -->
    <CandidateInsightsChips
      v-if="pro.candidateInsights"
      :insights="pro.candidateInsights"
      :prioritized-fields="prioritizedInsightFields"
    />
    <!-- Candidate Stats -->
    <ProPreviewStats class="mb-2" :pro="pro" :viewed-on="viewedOn" />
    <!-- Pro Applications -->
    <div v-if="jobApplicants" class="mb-2 text-xs font-normal leading-4.5 text-shade-800">
      <ProApplicantList :job-applicants="jobApplicants" :is-expanded="isExpanded" />
    </div>

    <!-- Candidate Details -->
    <div class="mt-4">
      <!-- Work Experience -->
      <div class="mt-3">
        <WorkExperienceList
          :pro="props.pro"
          :is-expanded="isExpanded"
          :suppress-highlights="suppressHighlights"
          data-test-id="pro-work-experience"
        />
      </div>

      <!-- Education -->
      <div class="mt-3">
        <EducationList :pro="props.pro" :is-expanded="isExpanded" data-test-id="pro-education" />
      </div>

      <!-- Industries are not indexed yet on Typesense: missing from the search results -->
    </div>
  </div>
</template>
<style scoped lang="postcss">
.skip-restore-button {
  @apply mx-0 border-none px-3 py-[6px]  shadow-none;
  & .btn-content {
    @apply flex items-center gap-2 text-shade-800;
  }
}

/* Scoped so it outranks the global .modal-button-primary colors, which win over utilities. */
.unlock-gold {
  @apply !border-2 !border-gold !bg-white !text-gold-brown hover:!bg-gold-bg-hover;
}

.unlock-gold :deep(.v-icon) {
  @apply !text-gold-brown;
}

:deep(.sub-title) {
  @apply mb-2.5 text-xs font-bold text-shade-800;

  .counter-text {
    @apply ml-2 font-normal text-highlight-600;
  }
}

.bulk-selection-checkbox {
  /* Touch users need visible controls, regardless of viewport width. */
  @apply ml-0 inline-block opacity-100 transition-all duration-300;

  @media (min-width: 640px) and (hover: hover) and (pointer: fine) {
    /* Explicitly hide the idle control when a wrapping header cannot clip it. */
    @apply ml-[-26px] opacity-0;
  }

  @media (any-pointer: coarse) {
    /* Hybrid devices must also support selection without hovering. */
    @apply ml-0 opacity-100;
  }
}
div[id^='pro-preview-card'] {
  @apply cursor-pointer rounded-xl border border-tint-60 bg-tint-0 p-4 transition-colors duration-300 sm:p-5;

  &:hover {
    @apply border-tint-200;
    box-shadow:
      0px 1px 2px -1px rgba(13, 10, 44, 0.1),
      0px 12px 16px -4px rgba(13, 10, 44, 0.03),
      0px 4px 6px -2px rgba(13, 10, 44, 0.03);

    .bulk-selection-checkbox {
      @apply ml-0 opacity-100;
    }
  }
}
</style>
